import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'ui-kit';
import { BUTTON_TYPES } from '_constants';
import { isEmpty } from 'lodash';
import * as Styled from './styles';

const VisitEditModal = ({ isOpen, onClose, onConfirm, initialValues, updatedValues, loading, allQuestions }) => {
  if (!isOpen) return null;

  const getQuestionLabel = (fieldName) => {
    const question = allQuestions[fieldName];
    return question?.title || question?.name || fieldName;
  };

  const getQuestionType = (fieldName) => {
    const question = allQuestions[fieldName];
    return question?.type || null;
  };

  const getChoiceText = (fieldName, value) => {
    const question = allQuestions[fieldName];

    if (!question || !question.choices) {
      return null;
    }

    if (Array.isArray(value)) {
      return value
        .map((val) => {
          // eslint-disable-next-line eqeqeq
          const choice = question.choices.find((c) => c.value == val);
          return choice ? choice.text : '';
        })
        .join(', ');
    }

    // eslint-disable-next-line eqeqeq
    const choice = question.choices.find((c) => c.value == value);
    return choice ? choice.text : '';
  };

  const compareChanges = (initialValue, updatedValue, type) => {
    const init = typeof initialValue === 'string' ? initialValue : JSON.stringify(initialValue);
    const updated = typeof updatedValue === 'string' ? updatedValue : JSON.stringify(updatedValue);
    if (type === 'file') {
      const content = init.match(/"content"\s*:\s*"([^"]*)"/);
      const fileLink = content ? content[1] : init;
      return fileLink !== updated;
    }
    if (
      [null, undefined, '', 'null', 'undefined'].includes(init) &&
      [null, undefined, '', 'null', 'undefined'].includes(updated)
    )
      return false;

    return init !== updated;
  };

  const getChangedFields = () => {
    const changes = [];

    Object.keys(updatedValues).forEach((key) => {
      const initialValue = initialValues[key];
      const updatedValue = updatedValues[key];

      const type = getQuestionType(key);
      // Compare values (handle different types)
      const isChanged = compareChanges(initialValue, updatedValue, type);

      const formatedInitialValue = formatValue({ value: initialValue, type });
      const formatedUpdatedValue = formatValue({ value: updatedValue, type });
      const initChoiceText = getChoiceText(key, initialValue, allQuestions);
      const updatedChoiceText = getChoiceText(key, updatedValue, allQuestions);

      if (isChanged) {
        changes.push({
          fieldName: key,
          fieldLabel: getQuestionLabel(key),
          initialValue: formatedInitialValue,
          updatedValue: formatedUpdatedValue,
          ...(!isEmpty(initChoiceText) && { initChoiceText }),
          ...(!isEmpty(updatedChoiceText) && { updatedChoiceText }),
          type,
        });
      }
    });

    return changes;
  };

  const formatValue = ({ value, type }) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (Array.isArray(value)) {
      if (type === 'file') {
        const [firstElem] = value;

        return firstElem?.name || '-';
      }
      return JSON.stringify(value);
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const changedFields = getChangedFields();

  return (
    <Styled.ModalOverlay onClick={onClose}>
      <Styled.ModalContent onClick={(e) => e.stopPropagation()}>
        <Styled.ModalHeader>
          <Styled.ModalTitle>Confirm Changes</Styled.ModalTitle>
          <Styled.ModalCloseButton onClick={onClose}>X</Styled.ModalCloseButton>
        </Styled.ModalHeader>

        <Styled.ModalBody>
          {changedFields.length === 0 ? (
            <Styled.NoChangesText>No changes detected</Styled.NoChangesText>
          ) : (
            <>
              <Styled.ChangesCount>
                {changedFields.length} field{changedFields.length !== 1 ? 's' : ''} changed
              </Styled.ChangesCount>

              <Styled.Table>
                <Styled.TableHeader>
                  <Styled.TableRow>
                    <Styled.TableSubHeaderCell>Field Name</Styled.TableSubHeaderCell>
                    <Styled.TableSubHeaderCell>Initial Value</Styled.TableSubHeaderCell>
                    <Styled.TableSubHeaderCell>Updated Value</Styled.TableSubHeaderCell>
                  </Styled.TableRow>
                </Styled.TableHeader>

                <Styled.TableBody>
                  {changedFields.map((field, index) => (
                    <Styled.TableRow key={index}>
                      <Styled.TableCell>
                        <Styled.FieldName>{field.fieldName}</Styled.FieldName>
                        <Styled.FieldLabel>{field.fieldLabel}</Styled.FieldLabel>
                      </Styled.TableCell>
                      <Styled.TableCell>
                        <Styled.FieldName>{`${field.initialValue}  ${
                          field.initChoiceText ? `(${field.initChoiceText})` : ''
                        }`}</Styled.FieldName>
                      </Styled.TableCell>

                      <Styled.TableCell isUpdated>
                        <Styled.FieldName>{`${field.updatedValue} ${
                          field.updatedChoiceText ? `(${field.updatedChoiceText})` : ''
                        }`}</Styled.FieldName>
                      </Styled.TableCell>
                    </Styled.TableRow>
                  ))}
                </Styled.TableBody>
              </Styled.Table>
            </>
          )}
        </Styled.ModalBody>

        <Styled.ModalFooter>
          <Button onClick={onClose} text="Cancel" disabled={loading} variant={BUTTON_TYPES.DANGER} />
          <Button
            onClick={onConfirm}
            text={loading ? 'Saving...' : 'Confirm & Save'}
            disabled={loading || changedFields.length === 0}
            variant={BUTTON_TYPES.DARK}
          />
        </Styled.ModalFooter>
      </Styled.ModalContent>
    </Styled.ModalOverlay>
  );
};

VisitEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  updatedValues: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  allQuestions: PropTypes.object,
};

VisitEditModal.defaultProps = {
  loading: false,
  allQuestions: {},
};

export default VisitEditModal;
